/* eslint-disable eqeqeq */
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import { copy, formatAmount } from '../utils/parse';
import ProductCarousel from './components/ProductCarousel';

import DefaultPhoto from '../assets/images/photo.png';

const SELL_TYPES = {
  NORMAL: 1,
  SIZE_COLORS: 2
}

const STATUS = {
  ACTIVE: 1,
  INACTIVE: 0
}

const ProductDetails = ({ showProduct, setShowProduct, showPrices, addToCart, setShowPreview, interestingProducts, admin }) => {
  const cart = useSelector(state => state.cart);
  const sizes = useSelector(state => state.sizes);
  const productCategories = useSelector(state => state.productCategories);
  const colors = useSelector(state => state.colors);

  // Determinamos la precisión decimal basada en la configuración del admin
  const decimals = admin?.config?.three_decimals === STATUS.ACTIVE ? 3 : 2;

  const product = copy(showProduct.product);

  const [currentPhoto, setCurrentPhoto] = useState(product.photo?.file || DefaultPhoto);

  const pricesWithUnit = product.prices?.filter(price => price.unit_description !== null) || [];
  const hasUnitPrices = pricesWithUnit.length > 0;

  const prodSizes = [...new Set(product?.size_colors?.filter((i) => i.size_id).map(i => i.size_id))];
  const prodColors = [...new Set(product?.size_colors?.filter((i) => i.color_id).map(i => i.color_id))];

  const isSelected = product.type == SELL_TYPES.SIZE_COLORS
    ? Boolean(cart.find(x => x.id === product.id && x.color_id == product.color_id && x.size_id == product.size_id))
    : Boolean(cart.find(x => x.id === product.id));

  const enoughStock = product.type === SELL_TYPES.SIZE_COLORS
    ? product.size_colors.some(x => x.stock > 0)
    : product.stock > 0;

  const size_color = product?.size_colors?.find((i) => {
    if (product.size_id && product.color_id) {
      return i.size_id == product.size_id && i.color_id == product.color_id;
    }
    else if (product.size_id) {
      return i.size_id == product.size_id && i.color_id == null;
    }
    else if (product.color_id) {
      return i.color_id == product.color_id && i.size_id == null;
    }
  });

  const showPreview = () => {
    setShowPreview({ show: true, product: product });
  }

  const handleSelectSize = (e) => {
    e.stopPropagation();
    setShowProduct(s => ({ ...s, product: { ...s.product, size_id: Number(e.target.value) } }));
  }

  const handleSelectColor = (e, value) => {
    e.stopPropagation();
    setShowProduct(s => ({ ...s, product: { ...s.product, color_id: Number(value) } }));
  }

  const handlePriceSelect = (value) => {
    setShowProduct(s => ({ ...s, product: { ...s.product, selected_price_id: Number(value) } }));
  }

  const minPrice = (product) => {
    if (Number(product.type) === SELL_TYPES.NORMAL) {
      if (product.default_price == 1) return product.sale_price;
      const priceObj = product.prices.find(price => price.default_price === 1);
      return priceObj ? priceObj.price : product.sale_price;
    }

    let prices = product.size_colors.filter(i => i.stock > 0).map(i => {
      const priceObj = i.prices.find(price => price.default_price === 1);
      return priceObj ? priceObj.price : i.sale_price;
    });
    return prices.length === 0 ? 0 : Math.min(...prices);
  }

  const handleAddToCart = (e) => {
    e.stopPropagation();
    const selectedPrice = product.prices?.find(p => p.id == product.selected_price_id);
    let updatedProduct = null;

    if (selectedPrice) {
      const updatedPrices = product.prices.map(p =>
        p.id == product.selected_price_id ? { ...p, default_price: 1 } : p
      );

      updatedProduct = {
        ...product,
        prices: updatedPrices,
        default_price: 2,
        selected_price: selectedPrice.price
      };
    }

    if (hasUnitPrices && !product.selected_price_id) {
      toast.error('Por favor seleccione un precio');
      return;
    }

    addToCart(updatedProduct || product);
  };

  /**
   * Componente de renderizado de precio corregido.
   * Desestructuramos { decimals } de las props.
   */
  const Price = ({ decimals }) => {
    console.log(decimals)
    if (!showPrices) return <></>;

    if (hasUnitPrices) {
      return (
        <select
          className="product-price font-medium leading-none"
          value={product.selected_price_id || ''}
          onChange={(e) => handlePriceSelect(e.target.value)}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <option value="" disabled hidden>Seleccione un precio</option>
          {pricesWithUnit.map((price) => (
            <option key={price.id} value={price.id}>
              {/* Ajuste: Pasamos decimals al formateador de opciones */}
              {formatAmount(price.price, decimals)} - {price.unit_description}
            </option>
          ))}
        </select>
      );
    }

    let precio = 0;
    if (Number(product.type) === SELL_TYPES.NORMAL) {
      if (product.default_price == 1) {
        precio = product.sale_price;
      } else {
        const priceObj = product.prices.find(price => price.default_price === 1);
        if (priceObj) precio = priceObj.price;
      }
    } else if (size_color?.sale_price) {
      const priceObj = size_color?.prices.find(price => price.default_price === 1);
      precio = priceObj ? priceObj.price : size_color?.sale_price;
    } else {
      precio = minPrice(product);
    }

    return (
      <h3 className="product-price text-2xl font-bold leading-none">
        {/* Ajuste: Pasamos decimals al formateador del precio principal */}
        {formatAmount(precio, decimals)}
      </h3>
    );
  }

  useEffect(() => {
    setCurrentPhoto(showProduct.product.photo?.file || DefaultPhoto);
  }, [showProduct]);

  return (
    <div className="relative z-10">
      <div className="fixed inset-0 bg-black bg-opacity-25" />
      <div className="fixed inset-0 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4 text-center">
          <div className="product-detail-modal w-full max-w-xl transform overflow-hidden rounded-2xl bg-white text-left align-middle shadow-xl transition-all">
            <div className="text-lg font-medium leading-6 px-5 py-4 text-gray-900 flex flex-row items-center justify-end">
              <div className="cursor-pointer" onClick={() => setShowProduct(s => ({ ...s, show: false }))}>
                <svg aria-hidden="true" className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"></path>
                </svg>
              </div>
            </div>

            <div className="px-2 pb-6">
              <div className="product-details">
                <div className="flex flex-col items-center">
                  <div className="product-image w-60 h-60">
                    <img
                      className="object-cover text-xs opacity-100"
                      src={process.env.REACT_APP_STORAGE + currentPhoto}
                      alt={product.name}
                      onClick={showPreview}
                      onError={el => el.target.src = DefaultPhoto}
                    />
                  </div>
                  {/* ... Resto de la galería de fotos ... */}
                </div>

                <div className="product-info">
                  <div>
                    <p className="text-lg lg:text-xl leading-5 lg:leading-6 pb-2">{product?.name}</p>
                    <div className="flex flex-col gap-1">
                      <h3 className="product-category" title="Categoría">
                        {productCategories.find(x => x.id === product.category_id)?.name}
                      </h3>
                      {/* Pasamos los decimales calculados al componente Price */}
                      <Price decimals={decimals} />
                    </div>

                    {
                      product.type == SELL_TYPES.SIZE_COLORS && (
                        <div className="product-size-color mt-4">
                          {
                            prodColors.length > 0 && (
                              <div style={{ display: 'flex' }}>
                                {
                                  prodColors.map((i) => {
                                    return (
                                      <div
                                        key={`color-${product.id}${i}${new Date().getMilliseconds()}`}
                                        onClick={ (e) => handleSelectColor(e, i) }
                                        style={{
                                          width: '20px',
                                          height: '20px',
                                          borderRadius: '10px',
                                          marginRight: '10px',
                                          marginBottom: '10px',
                                          cursor: 'pointer',
                                          backgroundColor: colors?.find(item => item.id == i)?.hex,
                                          borderWidth: 5,
                                          borderColor: product.color_id == i ? '#00000066' : 'transparent',
                                          boxShadow: '0px 1px 5px -2px #000000',
                                        }}></div>
                                    )
                                  })
                                }
                              </div>
                            )
                          }

                          {
                            prodSizes.length > 0 && (
                              <div className="flex flex-row justify-start gap-4">
                                <label className="font-medium">Talla:</label>
                                <select
                                  value={ product.size_id || '' }
                                  onChange={ handleSelectSize }
                                  onClick={ (e) => e.stopPropagation() }
                                >
                                  <option value="">Seleccione</option>
                                  {
                                    prodSizes.map((_item) => {
                                      const size = sizes.find(i => i.id == _item);

                                      return (
                                        <option
                                          key={`size-${product.id}${_item}${new Date().getMilliseconds()}`}
                                          value={ size?.id }
                                        >
                                          { size?.name }
                                        </option>
                                      )
                                    })
                                  }
                                </select>
                              </div>
                            )
                          }
                        </div>
                      )
                    }
                    {/* ... Resto de selectores de talla/color ... */}
                  </div>
                  <div>
                    {enoughStock ? (
                      <button className="add-product" onClick={handleAddToCart}>
                        {isSelected ? 'Añadido' : 'Añadir'}
                      </button>
                    ) : (
                      <div className="product-action inline-flex items-center">
                        <span className="text-sm text-red-600">Sin stock</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <p className="pt-4">{product.description_store}</p>
            </div>

            <ProductCarousel
              products={interestingProducts}
              cart={cart}
              addToCart={addToCart}
              showPrices={showPrices}
              setShowPreview={setShowPreview}
              setShowProduct={setShowProduct}
              identifier="from-product-details"
              interval={2}
              decimals={decimals}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default ProductDetails;